import React, { useMemo, Fragment, ReactElement } from 'react';
import Table from '../../../../components/table/table.component';
import TableHead from '../../../../components/table-head/table-head.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import FieldSelect from '../../../../components/field-select/field-select.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldCheckbox from '../../../../components/form-field-checkbox/form-field-checkbox.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import FormatService from '../../../../services/format-service';
import useScreenSmall from '../../../../hooks/use-screen-small';

const SettingsAccountSettingList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    accountSettingListData,
    settingList,
    formPrefix,
    nameColumnText,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const menuItemSettingList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: settingList,
  }), [settingList]);

  const tableAccountSettingFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'isVisible',
      title: 'Is Visible',
      isHidden: isSmallScreen,
      component: (accountSettingItem: AccountSettingItem, settingItem: Setting): ReactElement => (
        <TableCell isTextCenter={isEditMode}>
          {
            isEditMode ? (
              <FormFieldCheckbox withNoLabelOffset name={`${formPrefix}[${settingItem.name}].isVisible`} />
            ) : FormatService.formatBooleanValue(accountSettingItem.isVisible)
          }
        </TableCell>
      ),
    },
    {
      id: 2,
      name: 'defaultTitle',
      title: `Default ${nameColumnText}`,
      component: (accountSettingItem: AccountSettingItem, settingItem: Setting): ReactElement => (
        <TableCell isText>
          {
            isEditMode ? (
              <FieldSelect
                isPlain
                isBordered
                isDisabled
                withNoBorders
                id={`${formPrefix}[${settingItem.name}].name`}
                value={settingItem.name}
              >
                {menuItemSettingList}
              </FieldSelect>
            ) : settingItem.title
          }
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'title',
      title: nameColumnText,
      component: (accountSettingItem: AccountSettingItem, settingItem: Setting): ReactElement => (
        <TableCell isText>
          {
            isEditMode ? (
              <FormFieldText isBordered name={`${formPrefix}[${settingItem.name}].title`} />
            ) : accountSettingItem.title
          }
        </TableCell>
      ),
    },
  ], [isEditMode, nameColumnText]);

  return (
    <Table>
      <TableHead isSubHeader>
        <TableRow>
          {
            tableAccountSettingFields.map((tableField: TableField): ReactElement => (
              <TableCell key={tableField.id}>
                {tableField.title}
              </TableCell>
            ))
          }
        </TableRow>
      </TableHead>
      <TableBody isPlain withZebraRows withHeaderColumn>
        {
          settingList.map((settingItem: Setting): ReactElement => {
            const accountSetting: AccountSettingItem = accountSettingListData[settingItem.name];

            return accountSetting && (
              <TableRow key={settingItem.id}>
                {
                  tableAccountSettingFields.map((tableField: TableField): ReactElement | boolean => (
                    !!tableField.component && (
                      <Fragment key={tableField.id}>
                        {tableField.component(accountSetting, settingItem)}
                      </Fragment>
                    )
                  ))
                }
              </TableRow>
            );
          })
        }
      </TableBody>
    </Table>
  );
});

export default SettingsAccountSettingList;
